// views/Tarefas/permissoesSolicitacao.ts
//
// Regras de permissão da Solicitação, extraídas de TarefasView.tsx (isCreatorOrAdmin /
// canDirecionarSolicitacoes / canSelfAssignSolicitacao / podeVincularProjeto) pra serem
// reusadas fora dela — hoje só por SolicitacaoModalGlobal.tsx. TarefasView.tsx continua com
// sua própria cópia inline pros mesmos nomes (ela usa isCreatorOrAdmin também pra tarefa comum,
// que não tem lugar aqui) — mudou a regra aqui, mude lá também.
//
// temDepartamento é copiado (não importado) de src/lib/departamentos.ts de propósito: este
// arquivo tem teste que roda direto via `node --experimental-strip-types` (sem Vite), e um
// import de VALOR sem a extensão `.ts` só resolve através do bundler (ver CLAUDE.md) — o mesmo
// motivo pelo qual views/Gestao/Wizard/components/faturaToAnaliseTecnica.ts (também testado
// assim) só tem `import type` próprio, nenhum import de valor local. Mudou a regra lá, mude aqui
// também.
import type { Tarefa, Profile } from '../../types';

export type PerfilPermissao = Pick<Profile, 'role' | 'departamento' | 'departamentos'> & { id?: string | null };

const temDepartamento = (p: PerfilPermissao | null | undefined, dept: string): boolean =>
  !!p && (p.departamentos?.includes(dept) || p.departamento === dept);

// Equipe dona de cada departamento de solicitação e os departamentos do cadastro que formam
// essa equipe (#6518). Cópia de EQUIPES_SOLICITACAO (solicitacaoTipos.ts) pelo mesmo motivo do
// temDepartamento acima — mudou lá, mude aqui. Solicitação antiga sem departamento conta como
// Tecnologia e Marketing, que era a única fila antes das equipes.
const MEMBROS_DA_EQUIPE: Record<string, string[]> = {
  Tecnologia: ['Tecnologia', 'Marketing'],
  Marketing: ['Tecnologia', 'Marketing'],
  Financeiro: ['Financeiro', 'Pós Venda', 'Suporte e Atendimento'],
  Faturamento: ['Faturamento'],
  Comercial: ['Comercial'],
  'Suporte técnico': ['Suporte técnico'],
};
// Exceções da mesma cópia (#6596): `foraSe` e `pessoas` de EQUIPES_SOLICITACAO.
const FORA_DA_EQUIPE: Record<string, string[]> = { Faturamento: ['Suporte técnico'] };
const PESSOAS_DA_EQUIPE: Record<string, string[]> = { 'Suporte técnico': ['a40f054d-99d8-451d-a137-60485581fb27'] }; // Vinicius Alves cobre a Brenda

/** Equipes que avisam na tela e deixam qualquer pessoa trocar o responsável (#6544, #6552). */
export const EQUIPES_COM_AVISO = ['Financeiro', 'Faturamento', 'Comercial', 'Suporte técnico'];

/** A pessoa é da equipe que atende esta solicitação. */
export const daEquipeDaSolicitacao = (
  task: Pick<Tarefa, 'departamento_solicitacao'> | null | undefined,
  profile: PerfilPermissao | null | undefined,
): boolean => {
  const dep = task?.departamento_solicitacao || 'Tecnologia';
  if (profile?.id && PESSOAS_DA_EQUIPE[dep]?.includes(profile.id)) return true;
  return (MEMBROS_DA_EQUIPE[dep] || []).some(m => temDepartamento(profile, m))
    && !(FORA_DA_EQUIPE[dep] || []).some(m => temDepartamento(profile, m));
};

// Papéis que o RoleGuard de /tarefas/solicitacoes (App.tsx) deixa de fora. Vendedor é bloqueado
// também, salvo o do Comercial Interno (allowIf da mesma rota).
// motoboy: fora desde 29/10/2026 (#6518, decisão do Victor) — não tem equipe na Central.
const PAPEIS_FORA_DA_CENTRAL = ['motoboy', 'parceiro', 'vendedor_parceiro', 'empresa_parceira_admin', 'empresa_parceira_operador'];

/** A pessoa consegue abrir a Central de Solicitações (espelha o RoleGuard da rota). Serve para não
 *  oferecer em "Direcionar" quem não teria como atender — ex.: parceiros, que o cadastro põe no
 *  departamento Parcerias (equipe Comercial) mas não entram na Central. */
export const podeEntrarNaCentral = (profile: PerfilPermissao | null | undefined): boolean => {
  if (!profile) return false;
  if (PAPEIS_FORA_DA_CENTRAL.includes(profile.role || '')) return false;
  // Vendedor só entra se for do Comercial (antigo Comercial Interno; o cadastro unificou em 29/10/2026).
  if (profile.role === 'vendedor') return temDepartamento(profile, 'Comercial');
  return true;
};

/** Assumir ("Atribuir a mim"): qualquer pessoa da equipe que atende a solicitação. */
export const podeAssumirSolicitacao = (
  task: Pick<Tarefa, 'departamento_solicitacao'> | null | undefined,
  profile: PerfilPermissao | null | undefined,
): boolean => daEquipeDaSolicitacao(task, profile);

/** Direcionar/trocar responsável: adm ou gerente da equipe que atende a solicitação. */
export const podeDirecionarSolicitacao = (
  task: Pick<Tarefa, 'departamento_solicitacao'> | null | undefined,
  profile: PerfilPermissao | null | undefined,
): boolean => ['adm', 'gerente', 'gerente_parcerias'].includes(profile?.role || '') && daEquipeDaSolicitacao(task, profile);

/** Trocar o responsável: nas solicitações de Financeiro, Faturamento e Comercial qualquer pessoa
 *  (#6552); em Tecnologia/Marketing segue a regra de direcionar. */
export const podeTrocarResponsavel = (
  task: Pick<Tarefa, 'departamento_solicitacao'> | null | undefined,
  profile: PerfilPermissao | null | undefined,
): boolean => EQUIPES_COM_AVISO.includes(task?.departamento_solicitacao || '') || podeDirecionarSolicitacao(task, profile);

export interface PermissoesSolicitacao {
  isCreatorOrAdmin: boolean;
  canDirecionarSolicitacoes: boolean;
  canSelfAssignSolicitacao: boolean;
  podeVincularProjeto: boolean;
}

export const permissoesSolicitacao = (
  task: Pick<Tarefa, 'solicitante_id' | 'responsavel_id' | 'departamento_solicitacao'> | null,
  profile: PerfilPermissao | null | undefined,
  userId: string | undefined,
): PermissoesSolicitacao => {
  const role = profile?.role;
  const isCreatorOrAdmin = !!task && (
    task.solicitante_id === userId ||
    task.responsavel_id === userId ||
    ['adm', 'gerente', 'gerente_parcerias'].includes(role || '')
  );
  const canDirecionarSolicitacoes = podeDirecionarSolicitacao(task, profile);
  const canSelfAssignSolicitacao = podeAssumirSolicitacao(task, profile);
  const podeVincularProjeto = role === 'adm' || role === 'gerente';

  return { isCreatorOrAdmin, canDirecionarSolicitacoes, canSelfAssignSolicitacao, podeVincularProjeto };
};

// Contas que veem a Central mas não recebem pedidos (#6518, pedido do Victor em 29/10/2026): contas
// genéricas (Gerente Financeiro, Monitoramento, Vendedor) e pessoas que só acompanham (Igor Candido,
// Ludimilla Martins dos Santos, Giovanna Piethra). Saem do "Para quem" e da lista de Direcionar.
export const NAO_RECEBEM_PEDIDOS = new Set<string>([
  'ac245105-2cdb-42b4-aae6-724ba1faa73b', // Gerente Financeiro
  'a83d6b5d-a4a4-41cd-966b-282faa82231a', // Monitoramento
  '02f94e75-9b75-473d-b812-6b3aa61303a3', // Vendedor
  '07af78b6-768b-47ee-a4d4-8a626ebaebb3', // Igor Candido
  'e403a685-434d-43e1-a70a-f9eb86b15e14', // Ludimilla Martins dos Santos
  'b14f2695-3af3-49ad-af50-6e2103f3fe7c', // Giovanna Piethra Ferreira Freitas
]);

/** A pessoa pode ser escolhida como responsável de uma solicitação. */
export const podeReceberPedido = (u: { id?: string | null } | null | undefined): boolean =>
  !!u?.id && !NAO_RECEBEM_PEDIDOS.has(u.id);
